import { useFocusEffect } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import { StudentPortalShell } from "../../../components/student/StudentPortalShell";
import { TextLink } from "../../../components/aspects/TextLink";
import { useStudentPortalShellData } from "../../../lib/useStudentPortalShellData";
import {
  listMyInterviewOffers,
  markInterviewOfferViewed,
  respondToInterviewOffer,
  type InterviewOfferStatus,
  type StudentInterviewOffer,
} from "../../../lib/interviewOffers";


type ResponseDraft = { offerId: string; accept: boolean; message: string };

export default function StudentOffersScreen() {
  const shell = useStudentPortalShellData();
  const [offers, setOffers] = useState<StudentInterviewOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [responseDraft, setResponseDraft] = useState<ResponseDraft | null>(null);
  const [responding, setResponding] = useState(false);
  const [respondErr, setRespondErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const rows = await listMyInterviewOffers();
    setOffers(rows);
    setLoading(false);

    // Bump any 'extended' rows to 'viewed' on first appearance — silent
    // no-op if the RPC has already promoted (e.g., from another tab).
    const unread = rows.filter((r) => r.status === "extended");
    if (unread.length > 0) {
      await Promise.all(unread.map((r) => markInterviewOfferViewed(r.id)));
      // Refresh after the mark so the cards show the new status without a
      // visible flash. We don't refetch here because the change is purely
      // status='viewed', which the UI groups identically to 'extended'.
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload])
  );

  const grouped = useMemo(() => {
    const buckets: Record<"needs_response" | "accepted" | "past", StudentInterviewOffer[]> = {
      needs_response: [],
      accepted: [],
      past: [],
    };
    for (const o of offers) {
      if (o.status === "extended" || o.status === "viewed") buckets.needs_response.push(o);
      else if (o.status === "accepted") buckets.accepted.push(o);
      else buckets.past.push(o);
    }
    return buckets;
  }, [offers]);

  async function submitResponse() {
    if (!responseDraft) return;
    setResponding(true);
    setRespondErr(null);
    const { error } = await respondToInterviewOffer(
      responseDraft.offerId,
      responseDraft.accept,
      responseDraft.message.trim() || null
    );
    setResponding(false);
    if (error) {
      setRespondErr(error.message);
      return;
    }
    setResponseDraft(null);
    void reload();
  }

  return (
    <StudentPortalShell
      topbarTitle="Interview offers"
      topbarFirstName={shell.topbarFirstName}
      avatarName={shell.avatarName}
      avatarUrl={shell.avatarUrl}
      identity={shell.identity}
      casesBadgeCount={shell.casesBadgeCount}
      hideSidebar
    >
      <ScrollView style={{ flex: 1, backgroundColor: COLORS.OFFW.hex }} contentContainerStyle={{ paddingBottom: 64 }}>
        <View style={{ paddingHorizontal: 22, paddingTop: 28, maxWidth: 760 }}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.8,
              textTransform: "uppercase",
              color: COLORS.AMBER.hex,
            }}
          >
            Interview offers
          </Text>
          <Text
            style={{
              marginTop: 6,
              fontFamily: FONTS.display,
              fontSize: 36,
              lineHeight: 40,
              color: COLORS.FOREST.hex,
            }}
          >
            Companies that want to interview you
          </Text>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.body,
              fontSize: 13.5,
              lineHeight: 21,
              color: STUDENT_THEME.textOnSurfaceDim,
              maxWidth: 580,
            }}
          >
            Companies extend offers based on your competition performance and the roles you opted
            in for. Accept or decline right here — they'll be notified the moment you respond.
          </Text>

          {loading ? (
            <View style={{ marginTop: 28, alignItems: "center" }}>
              <ActivityIndicator color={COLORS.AMBER.hex} />
            </View>
          ) : offers.length === 0 ? (
            <View
              style={{
                marginTop: 28,
                paddingVertical: 32,
                paddingHorizontal: 22,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: STUDENT_THEME.border,
                backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                alignItems: "center",
              }}
            >
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: COLORS.FOREST.hex }}>
                No interview offers yet
              </Text>
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: STUDENT_THEME.textOnSurfaceDim,
                  textAlign: "center",
                  maxWidth: 460,
                }}
              >
                Companies extend offers after their competition's judging closes. Keep an eye out
                here once you've finished a program.
              </Text>
            </View>
          ) : (
            <View style={{ marginTop: 28, gap: 28 }}>
              <OffersGroup
                title="Needs response"
                tone="amber"
                offers={grouped.needs_response}
                expanded={expanded}
                setExpanded={setExpanded}
                onAcceptOrDecline={(offerId, accept) =>
                  setResponseDraft({ offerId, accept, message: "" })
                }
              />
              <OffersGroup
                title="Scheduled · accepted"
                tone="sage"
                offers={grouped.accepted}
                expanded={expanded}
                setExpanded={setExpanded}
                onAcceptOrDecline={null}
              />
              <OffersGroup
                title="Past · declined or withdrawn"
                tone="muted"
                offers={grouped.past}
                expanded={expanded}
                setExpanded={setExpanded}
                onAcceptOrDecline={null}
              />
            </View>
          )}
        </View>
      </ScrollView>

      {responseDraft ? (
        <ResponseModal
          accept={responseDraft.accept}
          message={responseDraft.message}
          onMessageChange={(m) => setResponseDraft({ ...responseDraft, message: m })}
          onCancel={() => {
            setResponseDraft(null);
            setRespondErr(null);
          }}
          onSubmit={() => void submitResponse()}
          submitting={responding}
          err={respondErr}
        />
      ) : null}
    </StudentPortalShell>
  );
}

function OffersGroup({
  title,
  tone,
  offers,
  expanded,
  setExpanded,
  onAcceptOrDecline,
}: {
  title: string;
  tone: "amber" | "sage" | "muted";
  offers: StudentInterviewOffer[];
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onAcceptOrDecline: ((offerId: string, accept: boolean) => void) | null;
}) {
  if (offers.length === 0) return null;
  const titleColor = tone === "amber" ? COLORS.AMBER.hex : tone === "sage" ? COLORS.SAGE.hex : STUDENT_THEME.textOnSurfaceDim;
  return (
    <View>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1.8,
          textTransform: "uppercase",
          color: titleColor,
          marginBottom: 10,
        }}
      >
        {title} · {offers.length}
      </Text>
      <View style={{ gap: 12 }}>
        {offers.map((o) => (
          <OfferCard
            key={o.id}
            offer={o}
            expanded={!!expanded[o.id]}
            onToggleExpand={() => setExpanded((p) => ({ ...p, [o.id]: !p[o.id] }))}
            onAcceptOrDecline={onAcceptOrDecline}
          />
        ))}
      </View>
    </View>
  );
}

function OfferCard({
  offer,
  expanded,
  onToggleExpand,
  onAcceptOrDecline,
}: {
  offer: StudentInterviewOffer;
  expanded: boolean;
  onToggleExpand: () => void;
  onAcceptOrDecline: ((offerId: string, accept: boolean) => void) | null;
}) {
  const showShort = !expanded && offer.message.length > 220;
  const visibleMessage = showShort ? offer.message.slice(0, 220) + "…" : offer.message;
  const deadline = offer.deadline_at ? new Date(offer.deadline_at) : null;
  const deadlineMs = deadline ? deadline.getTime() : null;
  const now = Date.now();
  const expired = deadlineMs != null && deadlineMs < now;
  const lt48h = deadlineMs != null && !expired && deadlineMs - now < 48 * 60 * 60 * 1000;

  return (
    <View
      style={{
        padding: 18,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: STUDENT_THEME.border,
        backgroundColor: STUDENT_THEME.surfaceRaisedBg,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <Text style={{ flex: 1, fontFamily: FONTS.displayMedium, fontSize: 22, color: COLORS.FOREST.hex }}>
          {offer.position.title}
        </Text>
        <StatusBadge status={offer.status} />
      </View>
      <Text style={{ marginTop: 4, fontFamily: FONTS.bodyMedium, fontSize: 13, color: COLORS.FOREST.hex }}>
        {offer.company.name}
      </Text>
      <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: STUDENT_THEME.textOnSurfaceFaint }}>
        Triggered by {offer.competition.name}
      </Text>

      <Text
        style={{
          marginTop: 12,
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 20,
          color: "rgba(28,43,26,0.85)",
        }}
      >
        {visibleMessage}
      </Text>
      {offer.message.length > 220 ? (
        <View style={{ marginTop: 4, alignSelf: "flex-start" }}>
          <TextLink
            theme={STUDENT_THEME}
            label={expanded ? "Show less" : "Show more"}
            onPress={onToggleExpand}
            tone="accent"
            showArrow={false}
          />
        </View>
      ) : null}

      {offer.proposed_times || offer.location_or_link ? (
        <View
          style={{
            marginTop: 12,
            paddingHorizontal: 12,
            paddingVertical: 10,
            borderRadius: 4,
            backgroundColor: "rgba(28,43,26,0.04)",
            gap: 4,
          }}
        >
          {offer.proposed_times ? (
            <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: STUDENT_THEME.textOnSurfaceDim }}>
              <Text style={{ fontFamily: FONTS.bodyBold }}>Proposed times: </Text>
              {offer.proposed_times}
            </Text>
          ) : null}
          {offer.location_or_link ? (
            <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: STUDENT_THEME.textOnSurfaceDim }}>
              <Text style={{ fontFamily: FONTS.bodyBold }}>Where: </Text>
              {offer.location_or_link}
            </Text>
          ) : null}
        </View>
      ) : null}

      {deadline ? (
        <View style={{ marginTop: 12, flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View
            style={{
              paddingHorizontal: 9,
              paddingVertical: 3,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: expired ? STUDENT_THEME.destructive : lt48h ? COLORS.AMBER.hex : "rgba(28,43,26,0.18)",
              backgroundColor: expired ? "rgba(192,57,43,0.06)" : lt48h ? "rgba(181,135,58,0.06)" : "transparent",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: expired ? STUDENT_THEME.destructive : lt48h ? COLORS.AMBER.hex : STUDENT_THEME.textOnSurfaceDim,
              }}
            >
              {expired ? "Expired" : "Respond by"}{" "}
              {deadline.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </Text>
          </View>
        </View>
      ) : null}

      {offer.status === "accepted" || offer.status === "declined" ? (
        offer.response_message ? (
          <View
            style={{
              marginTop: 12,
              paddingHorizontal: 12,
              paddingVertical: 10,
              borderRadius: 4,
              backgroundColor: STUDENT_THEME.successTintSoft,
              borderWidth: 1,
              borderColor: STUDENT_THEME.successBorderSoft,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: COLORS.SAGE.hex,
              }}
            >
              Your reply
            </Text>
            <Text
              style={{
                marginTop: 4,
                fontFamily: FONTS.body,
                fontSize: 12.5,
                lineHeight: 18,
                color: "rgba(28,43,26,0.85)",
              }}
            >
              {offer.response_message}
            </Text>
          </View>
        ) : null
      ) : null}

      {onAcceptOrDecline && !expired ? (
        <View style={{ marginTop: 14, flexDirection: "row", gap: 12 }}>
          <Pressable
            onPress={() => onAcceptOrDecline(offer.id, true)}
            style={[
              {
                paddingHorizontal: 18,
                paddingVertical: 10,
                borderRadius: 4,
                backgroundColor: COLORS.AMBER.hex,
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: COLORS.BLACK.hex,
              }}
            >
              Accept
            </Text>
          </Pressable>
          <Pressable
            onPress={() => onAcceptOrDecline(offer.id, false)}
            style={[
              {
                paddingHorizontal: 18,
                paddingVertical: 10,
                borderRadius: 4,
                borderWidth: 1,
                borderColor: "rgba(28,43,26,0.18)",
                backgroundColor: "transparent",
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: STUDENT_THEME.textOnSurfaceDim,
              }}
            >
              Decline
            </Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

function StatusBadge({ status }: { status: InterviewOfferStatus }) {
  const map: Record<InterviewOfferStatus, { label: string; color: string }> = {
    extended: { label: "Awaiting your response", color: COLORS.AMBER.hex },
    viewed: { label: "Awaiting your response", color: COLORS.AMBER.hex },
    accepted: { label: "Accepted", color: COLORS.SAGE.hex },
    declined: { label: "Declined", color: STUDENT_THEME.textOnSurfaceFaint },
    withdrawn: { label: "Withdrawn", color: STUDENT_THEME.textOnSurfaceFaint },
  };
  const m = map[status];
  return (
    <View
      style={{
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: m.color,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: m.color,
        }}
      >
        {m.label}
      </Text>
    </View>
  );
}

function ResponseModal({
  accept,
  message,
  onMessageChange,
  onCancel,
  onSubmit,
  submitting,
  err,
}: {
  accept: boolean;
  message: string;
  onMessageChange: (v: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  err: string | null;
}) {
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onCancel}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.55)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
            backgroundColor: COLORS.OFFW.hex,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: STUDENT_THEME.border,
            padding: 22,
          }}
        >
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: COLORS.FOREST.hex }}>
            {accept ? "Accept this offer?" : "Decline this offer?"}
          </Text>
          <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12.5, color: STUDENT_THEME.textOnSurfaceDim }}>
            {accept
              ? "The recruiter will be notified by email and your offer will move to your scheduled list."
              : "The recruiter will be notified by email. You can't undo a decline from this side."}
          </Text>
          <Text
            style={{
              marginTop: 14,
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: STUDENT_THEME.textOnSurfaceDim,
            }}
          >
            Anything to add? (optional)
          </Text>
          <TextInput
            value={message}
            onChangeText={onMessageChange}
            multiline
            placeholder={
              accept
                ? "Looking forward to it — Tuesdays generally work best for me."
                : "Thanks for the offer — I'm pursuing another path right now."
            }
            placeholderTextColor="rgba(28,43,26,0.30)"
            style={
              {
                marginTop: 6,
                minHeight: 100,
                backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                borderWidth: 1,
                borderColor: STUDENT_THEME.border,
                borderRadius: 4,
                paddingHorizontal: 12,
                paddingVertical: 10,
                color: COLORS.FOREST.hex,
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 19,
                textAlignVertical: "top",
                ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
              } as unknown as TextStyle
            }
          />

          {err ? (
            <Text style={{ marginTop: 8, fontFamily: FONTS.bodyMedium, fontSize: 12, color: STUDENT_THEME.destructive }}>{err}</Text>
          ) : null}

          <View style={{ marginTop: 18, flexDirection: "row", justifyContent: "flex-end", gap: 14 }}>
            <Pressable
              onPress={onCancel}
              style={[
                { paddingHorizontal: 14, paddingVertical: 10 },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: STUDENT_THEME.textOnSurfaceDim }}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={onSubmit}
              disabled={submitting}
              style={[
                {
                  paddingHorizontal: 22,
                  paddingVertical: 12,
                  borderRadius: 4,
                  backgroundColor: COLORS.AMBER.hex,
                  opacity: submitting ? 0.5 : 1,
                },
                Platform.OS === "web" && !submitting ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              {submitting ? (
                <ActivityIndicator size="small" color={COLORS.FOREST.hex} />
              ) : (
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: COLORS.BLACK.hex,
                  }}
                >
                  {accept ? "Accept offer" : "Decline offer"}
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}
